import { FC, useEffect, useMemo, useState } from 'react';
import Modal from '@components/Modal';
import Button from '@components/DesignLibrary/Button';
import useNoodleApi from '@hooks/useNoodleApi';
import * as tsClient from '@tsClient';
import { Select, DatePicker, Currency, Checkbox } from '@/components/DesignLibrary/Atoms';
import { IncomeMetadata } from '@documentsApi/document-api-models';

import { FlatConsolidatedData } from '@/lib/DocumentRequestUserDetailV3/Types';
import { ApiDefinition } from '@documentsApi';
import Trash from '@components/Icons/Trash';
import { CurrencyType, CurrencyValue } from '@/typings/api-models';
import { format as formatDateFns,subMonths } from 'date-fns';
import Plus from '@/components/Icons/Plus';

import Spacer from '@/components/Spacer';
import {incomeSourceOptions} from '../AddIncomeSourceModal';
import s from '../AddIncomeSourceModal/AddIncomeSourceModal.module.scss';

type FetchedDataResponse = ApiDefinition['/document-request-users/{documentRequestUserId}/consolidated-data']['GET']['response'];
type Props = {
  onClose: () => void;
  documentRequestUserId: string;
  refetchFiles: () => Promise<void>;
  row: FlatConsolidatedData & {
    rows: FlatConsolidatedData[];
  }
  fetchedData:FetchedDataResponse|null;
};

const EditNonEmploymentIncomeSourceModal: FC<Props> = ({ onClose, documentRequestUserId, refetchFiles, row, fetchedData }) => {
  const currentRecord = useMemo(() =>
    fetchedData?.files.find(f => f.id === row.id),
  [fetchedData, row]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [incomeSource, setIncomeSource] = useState<IncomeMetadata['incomeSource'] | null>(row.incomeSource ?? null);
  const [monthlyDollarValue, setMonthlyDollarValue] = useState<IncomeMetadata['monthlyDollarValue']>(currentRecord?.metadata?.monthlyDollarValue ?? []);
  const [SameMonthlyDollarValue, setSameMonthlyDollarValue] = useState<boolean>(false);
  const [isCtaDisabled, setIsCtaDisabled] = useState(true);

  const { getData: updateFn } = useNoodleApi(tsClient.documentRequests.updateDocumentRequestUserFile);

  const handleMonthlyDollarValueChange = (month?: string) => (e: CurrencyValue) => {
    setMonthlyDollarValue(prev =>
      prev.map(item => month
        ?item.month === month ? { ...item, income: e, month } : item
        : { ...item, income: e },
      ),
    );
  };

  const handleAddMonth = (): void => {
    setMonthlyDollarValue(prev => {
      let nextMonth;
      if (prev.length) {
        nextMonth = formatDateFns(subMonths(prev[prev.length - 1].month, 1), 'yyyy-MM-01');
      } else {
        nextMonth = formatDateFns(new Date(), 'yyyy-MM-01');
      }
      return [...prev, { income: { amount: 0, currency: 'USD' as CurrencyType, text: null }, month: nextMonth, useInCalculation:true, useInMeansTest:true }];
    });
  };

  const handleSubmit = async (): Promise<void> => {
    setIsSubmitting(true);
    const metadata = {
      ...(incomeSource ? { incomeSource } : {}),
      monthlyDollarValue,
    };
    await updateFn({fileId:row.id, id: documentRequestUserId, metadata});
    setIsSubmitting(false);
    refetchFiles();
    onClose();
  };

  useEffect(() => {
    const totalMonthlyDollarValue = monthlyDollarValue.reduce((acc, { income }) => {
      if (income && income.amount && typeof income.amount === 'number') {
        return acc + income.amount;
      }
      return acc;
    }, 0);
    setIsCtaDisabled(totalMonthlyDollarValue < 1);
  },[monthlyDollarValue]);

  return (
    <Modal hideBottomInset title="Edit Income Source" onClose={onClose} drag>
      <div className={s.container}>
        <Select
          label="Income Source"
          value={incomeSource}
          onChange={val => setIncomeSource(val as IncomeMetadata['incomeSource'])}
          options={incomeSourceOptions.filter(option => option.key !== 'Employment')}
        />
        <div>
          <Checkbox label="Same value of income for all months"
            isChecked={SameMonthlyDollarValue}
            onChange={e => {
              if(window.confirm("It will reset all month's income to $0.00")) {
                setSameMonthlyDollarValue(e);
                setMonthlyDollarValue(prev => prev.map(mdv => ({...mdv, income:{...mdv.income, amount: 0}}))); } }
            }
          />
          {SameMonthlyDollarValue
            ?<Currency label='Monthly dollar value' value={monthlyDollarValue.length && monthlyDollarValue[0]?.income || null}
              onChange={handleMonthlyDollarValueChange()}
              isEditor={true}
            />
            : <>
              <p className='body-sm-bold'>Enter income details</p>
              {!!monthlyDollarValue.length && <div className={s['monthly-income-list-header']}>
                <span className={s['month-col-sub-header']}>Month</span>
                <span className={s['income-col-sub-header']}>Income</span>
              </div>}
              {monthlyDollarValue.map((item, idx) =>
                <div className={s['income-list-container']} key={idx}>
                  <div className={s['month-col']} >
                    <DatePicker onChange={() => null} label='' value={item.month} showMonthYearPicker dateFormat='MMM yyyy'/>
                  </div>
                  <div className={s['income-col']} >
                    <Currency label='' value={item.income || null}
                      onChange={handleMonthlyDollarValueChange(item.month)}
                      isEditor={true}
                    />
                    <div className={s['delete-btn']} onClick={() => {
                      setMonthlyDollarValue(prev => prev.filter((_, index) => index !== idx));
                    }
                    }>
                      <Trash/>
                      <Spacer size={5}/>
                    </div>
                  </div>

                </div>,
              )}
              <Button variant="secondary" size="sm" onClick={handleAddMonth}>
                <Plus/> Add Month
              </Button>
            </>
          }
        </div>

        <Button loading={isSubmitting} variant="primary" size="md" fullWidth onClick={handleSubmit} disabled={isCtaDisabled}>
         Submit
        </Button>
      </div>
    </Modal>
  );
};

export default EditNonEmploymentIncomeSourceModal;
